import { computed, nextTick, ref } from 'vue';
import { eventCategories, events } from '@js/utils/event';
import { isMobileView, l, logEvent, qs, storedItemCreate, storedItemDelete, storedItemRead, urlParameterDelete, urlParameterGet, urlParameterSet } from '@js/utils/toolbox';
import { Dialog } from '@js/components/dialog';
import { resultsRender } from '@js/components/resultsRender';
import { v4 as uuid } from 'uuid';

const baseApiUrl = 'https://mcp.flightmate.com/api/v1';
// const baseApiUrl = 'http://localhost:4242/api/v1';
const STORAGE_KEY = 'chat_conversations_v1';

const isDebug = js_params.chatAdminToken || import.meta.env.DEV;

const isEnabled = window.js_params?.chatEnabled;

const messages = ref([]);

resultsRender.init();

// Conversation state
const loading = ref(false);
const conversationId = ref(urlParameterGet('id') || null);
const conversations = ref([]);
const previousResponseId = ref(null);

// Prevent duplicate title generation requests for the same conversation.
const pendingTitleRequests = new Set();

// --- LocalStorage helpers ----------------------------------------------------

function getStoredConversations () {
	try {
		return storedItemRead(STORAGE_KEY, 'sessionStorage') || [];
	} catch (e) {
		console.warn('Failed to parse stored conversations, resetting.', e);
		storedItemDelete(STORAGE_KEY, 'sessionStorage');
		return [];
	}
}

function saveConversations (list) {
	try {
		storedItemCreate(STORAGE_KEY, list, 'sessionStorage');
	} catch (e) {
		console.error('Failed to save conversations to sessionStorage:', e);
	}
}

function fallbackTitleFromMessage (text = '') {
	const cleaned = String(text)
		.replace(/\s+/g, ' ')
		.trim();

	if (!cleaned) {
		return 'New chat';
	}

	return cleaned.slice(0, 60) + (cleaned.length > 60 ? '…' : '');
}

function updateConversationMeta (id, meta = {}) {
	if (!id) {
		return;
	}

	const all = getStoredConversations();
	const existingIndex = all.findIndex(c => c.id === id);

	if (existingIndex === -1) {
		return;
	}

	all[existingIndex] = {
		...all[existingIndex],
		...meta,
		updatedAt: new Date().toISOString()
	};

	saveConversations(all);
	conversations.value = all;
}

async function generateConversationTitle ({ id, firstMessage }) {
	if (!id || !firstMessage || pendingTitleRequests.has(id)) {
		return;
	}

	pendingTitleRequests.add(id);

	try {
		const res = await fetch(`${baseApiUrl}/chat/summarise-title`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				message: firstMessage,
				language: window.js_params.language || 'en'
			})
		});

		if (!res.ok) {
			throw new Error(`Title endpoint failed with ${res.status}`);
		}

		const data = await res.json();

		updateConversationMeta(id, {
			title: data?.title || fallbackTitleFromMessage(firstMessage),
			summary: data?.summary || '',
			titleGenerated: Boolean(data?.title)
		});
	} catch (e) {
		console.warn('Failed to generate conversation title:', e);

		updateConversationMeta(id, {
			title: fallbackTitleFromMessage(firstMessage),
			summary: '',
			titleGenerated: false
		});
	} finally {
		pendingTitleRequests.delete(id);
	}
}

// Save the current messages/conversationId into sessionStorage list
function saveCurrentConversation () {
	if (!conversationId.value || !messages.value.length) {
		return;
	}

	const all = getStoredConversations();
	const now = new Date().toISOString();
	const existingIndex = all.findIndex(c => c.id === conversationId.value);
	const existing = existingIndex === -1 ? null : all[existingIndex];

	const firstUserMessage = messages.value.find(m => m.role === 'user');
	const fallbackTitle = fallbackTitleFromMessage(firstUserMessage?.content || 'New chat');

	const payload = {
		id: conversationId.value,
		title: existing?.title || fallbackTitle,
		summary: existing?.summary || '',
		titleGenerated: existing?.titleGenerated || false,
		createdAt: existingIndex === -1 ? now : existing.createdAt,
		updatedAt: now,
		messages: messages.value
	};

	if (existingIndex === -1) {
		all.unshift(payload);
	} else {
		all[existingIndex] = payload;
	}

	saveConversations(all);
	conversations.value = all;
}

// ------------------------------------------------------------------------

export function useChatApi (init = false) {
	function trackChatEvent (eventName, meta = '') {
		logEvent(eventName, eventCategories.chat, meta);
	}

	// ---- Streaming cancellation / guard state ---------------------------
	let activeAbortController = null;

	// Increments each time we cancel/start a new stream; old loops must stop/ignore
	const activeStreamRunId = ref(0);

	function cancelActiveStream (reason = 'cancel') {
		if (!loading.value) {
			return;
		}

		console.log(`Cancelling active stream: ${reason}`, { reason, activeAbortController, activeStreamRunId: activeStreamRunId.value });

		try {
			activeAbortController?.abort();
		} catch (_) {}

		activeAbortController = null;

		// Bump run id so any already-running loops self-terminate/ignore
		activeStreamRunId.value += 1;
		loading.value = false;
	}
	// --------------------------------------------------------------------

	if (init) {
		// Load conversations list from sessionStorage on init
		conversations.value = getStoredConversations();

		if (conversationId.value) {
			try {
				loadConversation(conversationId.value, 'init_url', false);
			} catch (e) {
				console.warn('Failed to load conversation from URL id, starting new.', e);
				newConversation('init_fallback', false);
			}
		}
	}

	async function positionLastMessageNearTop () {
		// Skip repositioning if this is the very first message
		if (messages.value.length === 1) {
			return;
		}

		// Wait for the new message to be painted before measuring
		await new Promise(resolve => requestAnimationFrame(resolve));

		const container = qs('.messages-container');
		const messagesContent = qs('.messages-content');
		const lastMessage = messagesContent?.lastElementChild;

		if (!container || !messagesContent || !lastMessage) {
			return;
		}

		// Reset any previously added min-height so measurements reflect true content size
		messagesContent.style.minHeight = '';

		// Wait for the browser to reflow after clearing min-height before measuring
		await new Promise(resolve => requestAnimationFrame(resolve));

		const containerHeight = container.clientHeight;

		// Target position: 33% from the top of the container
		const desiredTop = containerHeight * 0.10;

		// Get the last message's offset relative to the scrollable content
		const contentRect = messagesContent.getBoundingClientRect();
		const lastMessageRect = lastMessage.getBoundingClientRect();
		const lastMessageOffsetTop = lastMessageRect.top - contentRect.top;

		// Expand the content height so scrolling to (lastMessageOffsetTop - desiredTop)
		// is a valid scrollTop value — without this the browser clamps the scroll
		const requiredHeight = lastMessageOffsetTop - desiredTop + containerHeight;
		messagesContent.style.minHeight = `${requiredHeight}px`;

		// Smoothly scroll the last message into the desired position
		container.scrollTo({
			top: lastMessageOffsetTop - desiredTop,
			behavior: 'smooth'
		});
	}


	function resetScrollAdjustment () {
		const el = document.querySelector('.messages-content');

		if (el) {
			el.style.minHeight = '';
		}
	}

	async function sendMessage (text, source = 'input') {
		if (!isEnabled) {
			return;
		}
		trackChatEvent(
			source === 'preset' ? events.chatSendPreset : events.chatSendMessage,
			`${source}:${text.length}`
		);

		// Cancel any previous stream before starting a new one
		cancelActiveStream('new_send');

		loading.value = true;
		messages.value.push({ role: 'user', content: text });

		await positionLastMessageNearTop();

		const isNewConversation = messages.value.length === 1;

		if (isNewConversation) {
			conversationId.value = uuid();
			urlParameterSet('id', conversationId.value, true);
		}

		saveCurrentConversation();

		if (isNewConversation) {
			generateConversationTitle({
				id: conversationId.value,
				firstMessage: text
			});
		}

		const body = {
			history: messages.value,
			conversationId: conversationId.value,
			previousResponseId: previousResponseId.value,
			isMobile: isMobileView(),
			language: window.js_params.language || 'en'
		};

		// Controller + snapshot guards for this request
		activeAbortController = new AbortController();
		const myRunId = activeStreamRunId.value;
		const myConversationId = conversationId.value;

		let res;

		try {
			res = await fetch(`${baseApiUrl}/chat/completions`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body),
				signal: activeAbortController.signal
			});
		} catch (e) {
			if (e?.name === 'AbortError') {
				return;
			}

			loading.value = false;
			throw e;
		}

		try {
			await processStream(res.body, {
				runId: myRunId,
				conversationId: myConversationId,
				signal: activeAbortController.signal
			});
		} finally {
			// Only clear if this request is still the active one
			if (myRunId === activeStreamRunId.value) {
				activeAbortController = null;
				loading.value = false;
			}
		}
	}

	async function processStream (stream, ctx) {
		const reader = stream?.getReader();
		const decoder = new TextDecoder();

		// These need to be mutable objects that persist across iterations
		const state = {
			lineBuffer: ''
		};

		while (true) {
			// If a new stream started, conversation changed, or aborted, stop reading
			if (ctx.runId !== activeStreamRunId.value) {
				try {
					await reader?.cancel();
				} catch (_) {}
				break;
			}

			if (ctx.conversationId !== conversationId.value) {
				try {
					await reader?.cancel();
				} catch (_) {}
				break;
			}

			if (ctx.signal?.aborted) {
				try {
					await reader?.cancel();
				} catch (_) {}
				break;
			}

			let result;

			try {
				result = await reader.read();
			} catch (e) {
				if (e?.name === 'AbortError') {
					break;
				}

				throw e;
			}

			const { value, done } = result;

			if (done) {
				break;
			}

			const chunk = decoder.decode(value, { stream: true });
			state.lineBuffer += chunk;

			const lines = state.lineBuffer.split('\n');
			state.lineBuffer = lines.pop() || '';

			for (const line of lines) {
				// Ignore lines if this run is no longer current
				if (ctx.runId !== activeStreamRunId.value) {
					break;
				}

				await handleStreamLine(line, state);
			}
		}
	}

	function getLastMessage () {
		return messages.value[messages.value.length - 1] || null;
	}

	function startAssistantMessage (text = '') {
		const msg = {
			role: 'assistant',
			content: text
		};

		messages.value.push(msg);
		return msg;
	}

	function appendToAssistantMessage (text) {
		let last = getLastMessage();

		// If there is no last message or it's not an assistant message, start a new one
		if (!last || last.role !== 'assistant' || ['mcp_call_started', 'mcp_call_completed'].includes(last.type)) {
			last = startAssistantMessage(text);
		} else {
			last.content += text;
		}

		return last;
	}

	function resolveIatas (iatas) {
		return fetch(`${baseApiUrl}/iataAutoComplete`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ iatas })
		})
			.then(res => res.json())
			.then(data => data.map((item) => {
				let text = '';

				if (item.name) {
					text += item.name.trim();
				}

				if (item.country) {
					text += `, ${item.country}`;
				}

				return text.trim();
			}).join(', '));
	}

	// Local version: just read from sessionStorage
	function loadConversations () {
		conversations.value = getStoredConversations();
	}

	// Local version: load one conversation from sessionStorage
	async function loadConversation (id, source = 'recent_list', track = true) {
		if (track) {
			trackChatEvent(events.chatLoadConversation, source);
		}

		const all = getStoredConversations();
		const conv = all.find(c => c.id === id);

		if (!conv) {
			throw new Error('Conversation not found');
		}

		messages.value = conv.messages || [];
		conversationId.value = id;

		urlParameterSet('id', id, true);

		await nextTick();

		// Scroll to bottom
		qs('.messages-container')?.scrollTo(0, qs('.messages-container')?.scrollHeight || 0);
	}

	async function deleteConversation (id, source = 'recent_list', track = true) {
		// Stop any stream before deleting/swapping state
		cancelActiveStream('delete_conversation');

		if (track) {
			trackChatEvent(events.chatDeleteConversation, source);
		}

		const dialog = new Dialog(
			l('chat.delete_conversation.title'),
			l('chat.delete_conversation.message'),
			{ cancelOnClickout: true }
		);

		const response = await dialog.show();

		if (response === 'cancel') {
			return;
		}

		const all = getStoredConversations();
		const updated = all.filter(c => c.id !== id);

		saveConversations(updated);
		conversations.value = updated;

		if (conversationId.value === id) {
			await newConversation('delete_active_conversation', false);
		}
	}

	async function handleStreamLine (line, state) {
		// 1. Basic SSE format guard
		if (!line.startsWith('data:')) {
			return;
		}

		const json = line.slice(5).trim();

		// Ignore empty keep-alive lines or [DONE] markers
		if (!json || json === '[DONE]') {
			return;
		}

		let evt;

		try {
			evt = JSON.parse(json);
		} catch (e) {
			console.warn('Failed to parse stream JSON:', json, e);
			return;
		}

		switch (evt.type) {
			case 'response_id': {
				previousResponseId.value = evt.responseId || null;
				break;
			}

			case 'mcp_call_started':
			case 'mcp_call_completed': {
				console.log('MCP Call event', evt);

				// Normalize mcp messages a bit so UI can treat everything as "messages"
				upsertMcpCallMessage(evt);
				break;
			}

			case 'assistant_message': {
				// This is a full assistant message (non-streamed or final text)
				startAssistantMessage(evt.text || '');
				await nextTick(); // let UI scroll, etc.
				break;
			}

			case 'token': {
				// Streamed token – append to current assistant message
				appendToAssistantMessage(evt.text || '');
				break;
			}

			case 'chunk_end': {
				loading.value = false;

				// Persist assistant message
				saveCurrentConversation();
				break;
			}

			default: {
				console.log('Unhandled stream event:', evt);
			}
		}
	}

	function getToolCallId (evt) {
		return evt?.content?.call_id || `${evt.content?.name}:${JSON.stringify(evt.content?.arguments || {})}`;
	}

	function upsertMcpCallMessage (evt) {
		const callId = getToolCallId(evt);

		const existingIndex = messages.value.findIndex(m => m.role === 'assistant' && m.type?.startsWith('mcp_call') && m.content?.call_id === callId);

		const normalized = {
			...evt,
			role: 'assistant',
			type: evt.type,
			content: {
				...evt.content,
				call_id: callId
			}
		};

		if (existingIndex === -1) {
			messages.value.push(normalized);
		} else {
			const existingMessage = messages.value[existingIndex];

			existingMessage.type = normalized.type;
			existingMessage.role = normalized.role;
			existingMessage.content = {
				...(existingMessage.content || {}),
				...normalized.content
			};
		}
	}

	function newConversation (source = 'header_button', track = true) {
		if (track) {
			trackChatEvent(events.chatNewConversation, source);
		}

		resetScrollAdjustment();

		// Stop any active stream immediately
		cancelActiveStream('new_conversation');


		previousResponseId.value = null;
		conversationId.value = null;
		messages.value = [];
		loading.value = false;

		urlParameterDelete('id', true);
	}

	window.addEventListener('popstate', async () => {
		console.log('Popstate event detected, checking conversation ID in URL…');

		// If navigation changes chat, stop any active stream
		cancelActiveStream('popstate');

		const id = urlParameterGet('id');

		if (id === conversationId.value) {
			console.log('Conversation ID unchanged.');
			return;
		}

		if (!id) {
			conversationId.value = null;
			messages.value = [];
			return;
		}

		try {
			await loadConversation(id, 'popstate', false);
		} catch (e) {
			console.warn('Conversation ID invalid, resetting…', e);
			newConversation('popstate_invalid_id', false);
		}
	});

	return {
		isDebug,
		isEnabled,
		resolveIatas,
		messages,
		loading,
		loadConversation,
		deleteConversation,
		loadConversations,
		conversationId,
		conversations,
		newConversation,
		sendMessage,
		baseApiUrl,
		positionLastMessageNearTop
	};
}

const queries = {
	en: [
		// 'What can you help me with? 🤖',
		'I want to go to Asia for two weeks ✈️',
		'Find me a budget trip under 3000 SEK 💸',
		'Help me find somewhere new to go 🧭',
		'I want to go somewhere warm and sunny ☀️',
		'I want to visit a European city for a weekend 🏙️',
		'I bought a flight and haven\'t received my ticket yet. What should I do? ✈️',
		'Best travel deals right now? 🔥'
	],
	sv: [
		// 'Vad kan du hjälpa mig med? 🤖',
		'Jag vill åka till Asien i två veckor ✈️',
		'Hitta en budgetresa under 3000 SEK 💸',
		'Hjälp mig att hitta någonstans nytt att åka 🧭',
		'Jag vill åka någonstans varmt och soligt ☀️',
		'Jag vill besöka en europeisk stad över en helg 🏙️',
		'Jag har köpt en flygbiljett men har inte fått min biljett än. Vad ska jag göra? ✈️',
		'Bästa resetipsen just nu? 🔥'
	]
};

// const queries = [
// Vad kan du hjälpa mig med? 🤖
// 	l('chat.presets.0'),
// 'Jag vill åka till Asien i två veckor ✈️',
// 	l('chat.presets.1'),
// Hitta en budgetresa under 2000 SEK 💸
// 	l('chat.presets.2'),
// Hjälp mig att hitta någonstans nytt att åka 🧭
// 	l('chat.presets.3'),
// Jag vill åka någonstans varmt och soligt ☀️
// 	l('chat.presets.4'),
// Jag vill besöka en europeisk stad över en helg 🏙️
// 	l('chat.presets.5'),
// Jag har köpt en flygbilett men har inte fått min biljett än. Vad ska jag göra? ✈️
// 	l('chat.presets.6'),
// Bästa resetipsen just nu? 🔥
// 	l('chat.presets.7')
// ];

/**
 * This composable manages the preset queries shown to the user as buttons they can click on to quickly populate the input with common queries. It decides which presets to show based on the conversation state:
 * - On the first turn, it shows a fixed list of preset queries.
 * - On subsequent turns, if there is a recent assistant message that contains a `displayMultipleOptions` tool call, it shows those options as presets.
 * @param language
 * @returns
 */
export function usePresetQueries (language = js_params.language) {
	const presetQueries = ref(queries[language] || queries.en);

	const multipleOptionsMessage = computed(() => {
		for (let i = messages.value.length - 1; i >= 0; i--) {
			const message = messages.value[i];

			// Stop once we hit a user message
			if (message.role === 'user') {
				return null;
			}

			// Find the most recent assistant message that contains a displayMultipleOptions tool call with options
			if (
				message.role === 'assistant'
				&& message.content?.name === 'displayMultipleOptions'
				&& Array.isArray(message.content?.arguments?.options)
			) {
				return message;
			}
		}

		return null;
	});

	const presets = computed(() => {
		// On the first turn show preset queries.
		if (!messages.value.length) {
			return presetQueries.value;
		}

		// Wait until the current message has stopped streaming
		if (loading.value) {
			return [];
		}

		// If the latest assistant message contains a displayMultipleOptions tool call, show those options as presets for the user to click on.
		return multipleOptionsMessage.value?.content?.arguments?.options || [];
	});

	return {
		presets
	};
}
